import type {
  ProductListV2,
  ProductMembership,
} from '@lib/types/api/ProductListV2';

import {
  toListData,
  type CommitListChangesParams,
} from './productListsHelpers';

export type SaveForLaterResult = {
  saved: boolean;
  error?: {
    code: 'UNAUTHENTICATED' | 'LIST_UNAVAILABLE' | 'NETWORK_ERROR' | 'UNKNOWN';
    message: string;
  };
};

export type SaveForLaterErrorCode = NonNullable<
  SaveForLaterResult['error']
>['code'];

export type OnSaveForLater = (params: {
  productId: string;
}) => Promise<SaveForLaterResult>;

// Fixed strings so no server detail, token or claim can leak to the caller.
const ERROR_MESSAGES: Record<SaveForLaterErrorCode, string> = {
  UNAUTHENTICATED: 'User is not signed in',
  LIST_UNAVAILABLE: 'Save-for-later list is unavailable',
  NETWORK_ERROR: 'Network request failed',
  UNKNOWN: 'Could not save the product for later',
};

export const saveForLaterFailure = (
  code: SaveForLaterErrorCode
): SaveForLaterResult => ({
  saved: false,
  error: { code, message: ERROR_MESSAGES[code] },
});

export const classifySaveForLaterError = (
  error: unknown
): SaveForLaterErrorCode => {
  if ((error as { status?: unknown } | null)?.status === 401) {
    return 'UNAUTHENTICATED';
  }
  // fetch rejects with a TypeError only when no response arrived.
  if (error instanceof TypeError) return 'NETWORK_ERROR';
  return 'UNKNOWN';
};

export type SaveForLaterDeps = {
  resolveList: () => Promise<ProductListV2 | undefined>;
  readMembership: (productId: string) => Promise<ProductMembership | null>;
  lookupMembership: (productId: string) => Promise<ProductMembership>;
  commit: (params: CommitListChangesParams) => Promise<void>;
};

const isInList = (membership: ProductMembership, listId: string) =>
  membership.personal.includes(listId);

export async function saveProductForLater(
  productId: string,
  { resolveList, readMembership, lookupMembership, commit }: SaveForLaterDeps
): Promise<SaveForLaterResult> {
  if (!productId) return saveForLaterFailure('UNKNOWN');

  let list: ProductListV2 | undefined;
  try {
    list = await resolveList();
  } catch (error) {
    return saveForLaterFailure(classifySaveForLaterError(error));
  }
  if (!list) return saveForLaterFailure('LIST_UNAVAILABLE');

  let before = await readMembership(productId).catch(() => null);
  // A cached hit can predate a removal elsewhere, and a false positive makes
  // the caller drop the cart line, so only a fresh read may skip the write.
  if (before && isInList(before, list.id)) {
    const fresh = await lookupMembership(productId).catch(() => null);
    if (fresh && isInList(fresh, list.id)) return { saved: true };
    before = fresh;
  }

  try {
    await commit({
      product: { id: productId, name: '' },
      listsToAdd: [toListData(list, true)],
      idsToRemove: [],
      membership: before
        ? {
            personal: Array.from(new Set([...before.personal, list.id])),
            organization: before.organization,
          }
        : undefined,
    });
    return { saved: true };
  } catch (writeError) {
    // A repeat add fails with an untagged 500, so only a fresh read can tell
    // "already there" from a real failure.
    const after = await lookupMembership(productId).catch(() => null);
    if (after && isInList(after, list.id)) return { saved: true };
    return saveForLaterFailure(classifySaveForLaterError(writeError));
  }
}
